import React, { useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@mui/material';

import { getPolicyDecisionExplanationEndPoint } from '../PPWB/api.endPoints';
import { HttpService } from '../../common/http.service';
import styles from './DecisionExplanation.module.scss';

export interface DecisionExplanationRiskItem {
  riskName?: string;
  evidenceSource?: string;
  outcomeDecision?: string;
  outcomeHealthClass?: string;
  ranking?: number;
  ruleNumber?: number;
  ruleModifier?: string;
  ruleMessage?: string;
  additionalInfo?: string;
  inExternalView?: boolean;
  outcomeDebits?: number;
}

export interface DecisionExplanationResponse {
  declined?: DecisionExplanationRiskItem[];
  approvedStandard?: DecisionExplanationRiskItem[];
  customDecisionReason?: DecisionExplanationRiskItem | null;
  decisionChangeReasons?: string[];
  riskNamesForRateClass?: string[];
}

const FIELD_LABELS: [keyof DecisionExplanationRiskItem, string][] = [
  ['riskName', 'Risk name'],
  ['ruleNumber', 'Rule number'],
  ['ranking', 'Ranking'],
  ['evidenceSource', 'Evidence source'],
  ['outcomeDecision', 'Outcome decision'],
  ['outcomeHealthClass', 'Outcome health class'],
  ['inExternalView', 'In external view'],
  ['additionalInfo', 'Additional info'],
];

const hasValue = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== '';

const formatValue = (value: unknown): string => {
  if (!hasValue(value)) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

export const fetchDecisionExplanation = async (
  product: string,
  carrier: string,
  policyId: string,
): Promise<DecisionExplanationResponse | null> => {
  const response = await HttpService.getInstance().get(
    getPolicyDecisionExplanationEndPoint(product, carrier, policyId),
  );
  if (response && !response.error && response.parsedBody) {
    return response.parsedBody;
  }
  return null;
};

const RiskItemCard = ({ item }: { item: DecisionExplanationRiskItem }) => (
  <div className={styles.Card}>
    {FIELD_LABELS.map(([key, label]) => (
      <div key={key} className={styles.Field}>
        <Typography variant="caption" className={styles.CardLabel}>
          {label}
        </Typography>
        <Typography variant="body2" fontSize={13}>
          {formatValue(item[key])}
        </Typography>
      </div>
    ))}
  </div>
);

/**
 * Nested accordion rendered inside a single Linked Policies row (LinkedPoliciesItem) - keyed by
 * that row's own policy, not the application currently under review, since most rows have no
 * linked Nora application at all.
 */
export const DecisionExplanation = ({
  carrier,
  product,
  policyId,
}: {
  carrier: string | undefined;
  product: string | undefined;
  policyId: number | undefined;
}) => {
  const [expanded, setExpanded] = useState(false);
  const canFetch = Boolean(carrier && product && policyId);

  const { data, isLoading } = useQuery({
    queryKey: ['decisionExplanation', carrier, product, policyId],
    queryFn: () =>
      fetchDecisionExplanation(
        product as string,
        carrier as string,
        String(policyId),
      ),
    enabled: expanded && canFetch,
  });

  if (!canFetch) {
    return null;
  }

  const declined = data?.declined ?? [];
  const approvedStandard = data?.approvedStandard ?? [];
  const riskItems = [...declined, ...approvedStandard];
  const hasData = riskItems.length > 0;

  return (
    <Accordion
      className={styles.Accordion}
      sx={{ boxShadow: 'none', marginBottom: 2 }}
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disableGutters
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        className={styles.AccordionSummary}
      >
        <Typography variant="overline" className={styles.SectionLabel}>
          Decision Explanation
        </Typography>
      </AccordionSummary>
      <AccordionDetails className={styles.AccordionDetails}>
        {isLoading && (
          <Typography variant="body2" color="text.secondary">
            Loading decision explanation…
          </Typography>
        )}
        {!isLoading && !hasData && (
          <Typography variant="body2" color="text.secondary">
            No decision explanation available for this policy.
          </Typography>
        )}
        {!isLoading && hasData && (
          <div
            className={styles.CardCarousel}
            role="region"
            aria-label="Decision explanation rule results"
            tabIndex={0}
          >
            {riskItems.map((item, index) => (
              <RiskItemCard
                key={`${item.riskName ?? 'risk'}-${index}`}
                item={item}
              />
            ))}
          </div>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
